import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BoardAbilities, BoardPermission, ColumnType, ProjectHealth, ProjectOverview, UpdateProject,
} from '@pm/shared';
import { api } from '@/lib/api';
import { keys } from '@/lib/query';
import type { DepartmentAssignment } from './PeoplePicker';

export interface StatusLabel {
  id: string; label: string; color: string; isDefault: boolean;
  /** Items on this label count as complete in every report. */
  isDoneState: boolean;
}
export interface ColumnDefinition {
  id: string; name: string; slug: string; type: ColumnType;
  position: number; isRequired: boolean; isVisible?: boolean;
  width?: number | null;
  settings?: Record<string, unknown> | null;
  labels: StatusLabel[];
}
export interface BoardItem {
  id: string;
  /** Per-board sequential number, shown by an `item_id` column. */
  shortId: number | null;
  name: string;
  values: Record<string, unknown>;
  position: number;
  createdById: string | null;
  assignees: { user: { id: string; name: string; avatarUrl: string | null } }[];
  _count: { comments: number; subitems: number };
}
export interface BoardGroup {
  id: string; name: string; color: string | null; position: number; items: BoardItem[];
}
export interface BoardView {
  id: string; name: string; workspaceId: string;
  folderId: string | null;
  isPrivate: boolean;
  permission: BoardPermission;
  createdById: string | null;
  /** A project has an Overview tab. */
  isProject: boolean;
  healthOverride: ProjectHealth | null;
  /** Width of the task-name column in the table; null means the default. */
  nameWidth: number | null;
  columns: ColumnDefinition[];
  groups: BoardGroup[];
  /** What the person looking may do here - decided by the API, mirrored by the page. */
  abilities: BoardAbilities;
}

type Person = { id: string; name: string; avatarUrl: string | null };

export interface BoardAccessInfo {
  isPrivate: boolean;
  permission: BoardPermission;
  createdById: string | null;
  createdBy: Person | null;
  owners: Person[];
  members: Person[];
  abilities: BoardAbilities;
}

/**
 * Whether one row is editable. On an "edit own items" board that is the
 * rows someone created or is assigned to - the API applies the same rule.
 */
export function canEditItem(
  abilities: BoardAbilities, userId: string | undefined,
  item: Pick<BoardItem, 'createdById' | 'assignees'>,
) {
  if (abilities.editItems) return true;
  if (!abilities.editOwnItems || !userId) return false;
  return item.createdById === userId || item.assignees.some((a) => a.user.id === userId);
}

export const useBoardAccess = (boardId: string, enabled = true) =>
  useQuery({
    queryKey: keys.boardAccess(boardId),
    queryFn: () => api.get<BoardAccessInfo>(`/boards/${boardId}/access`),
    enabled: Boolean(boardId) && enabled,
  });

export function useUpdateBoardAccess(boardId: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      permission?: BoardPermission; ownerIds?: string[]; isPrivate?: boolean; memberIds?: string[];
    }) => api.put<BoardAccessInfo>(`/boards/${boardId}/access`, body),
    onSuccess: (info) => {
      qc.setQueryData(keys.boardAccess(boardId), info);
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
      void qc.invalidateQueries({ queryKey: keys.boards(workspaceId) });
      void qc.invalidateQueries({ queryKey: ['boards', boardId, 'mentionable'] });
    },
  });
}

/** The creator hands the board to another member; afterwards they may only view it. */
export function useTransferBoard(boardId: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<BoardAccessInfo>(`/boards/${boardId}/transfer`, { userId }),
    onSuccess: (info) => {
      qc.setQueryData(keys.boardAccess(boardId), info);
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
      void qc.invalidateQueries({ queryKey: keys.boards(workspaceId) });
    },
  });
}

/** Who the @ list offers on this board - on a private one, only the people who can open it. */
export const useBoardMentionable = (boardId: string) =>
  useQuery({
    queryKey: ['boards', boardId, 'mentionable'],
    queryFn: () => api.get<Person[]>(`/boards/${boardId}/mentionable`),
    enabled: Boolean(boardId),
    staleTime: 5 * 60_000,
  });

export const useBoard = (boardId: string) =>
  useQuery({
    queryKey: keys.board(boardId),
    queryFn: () => api.get<BoardView>(`/boards/${boardId}/view`),
    enabled: Boolean(boardId),
  });

/** Progress, health and workload, worked out by the API from the decrypted values. */
export const useProjectOverview = (boardId: string) =>
  useQuery({
    queryKey: keys.projectOverview(boardId),
    queryFn: () => api.get<ProjectOverview>(`/boards/${boardId}/overview`),
    // Tasks change on the other tabs; coming back here should show that.
    staleTime: 0,
  });

/** Make the board a project (adding the columns it lacks), or set its health by hand. */
export function useUpdateProject(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProject) =>
      api.put<{ isProject: boolean; healthOverride: ProjectHealth | null; addedColumns: string[] }>(
        `/boards/${boardId}/project`, body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
      void qc.invalidateQueries({ queryKey: keys.projectOverview(boardId) });
    },
  });
}

export function useCreateItem(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { groupId: string; name: string }) => api.post<BoardItem>('/items', { ...body, values: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.board(boardId) }),
  });
}

export interface ItemDetail extends BoardItem {
  description: string | null;
  boardId: string;
  isArchived: boolean;
  createdAt: string;
  board: { id: string; name: string; workspaceId: string };
  group: { id: string; name: string; color: string | null };
  subitems: BoardItem[];
  _count: { comments: number; timeLogs: number; subitems: number };
}
export interface ItemComment {
  id: string; content: string; createdAt: string;
  /** Ids of the people @mentioned in the text. */
  mentions: string[] | null;
  user: { id: string; name: string; avatarUrl: string | null };
  replies: ItemComment[];
}
export interface ItemActivityEntry {
  id: string; actionType: string; oldValue: string | null; newValue: string | null;
  metadata: Record<string, unknown> | null; createdAt: string;
  user: { id: string; name: string; avatarUrl: string | null } | null;
}
export interface ItemTimeLog {
  id: string; startedAt: string; endedAt: string | null;
  durationSeconds: number | null; notes: string | null; isManual: boolean;
  user: { id: string; name: string; avatarUrl: string | null };
}

export const useItem = (itemId: string | null) =>
  useQuery({
    queryKey: ['items', itemId],
    queryFn: () => api.get<ItemDetail>(`/items/${itemId}`),
    enabled: Boolean(itemId),
  });

export const useItemComments = (itemId: string | null) =>
  useQuery({
    queryKey: ['items', itemId, 'comments'],
    queryFn: () => api.get<ItemComment[]>(`/items/${itemId}/comments`),
    enabled: Boolean(itemId),
  });

export const useItemActivity = (itemId: string | null) =>
  useQuery({
    queryKey: ['items', itemId, 'activity'],
    queryFn: () => api.get<ItemActivityEntry[]>(`/items/${itemId}/activity`),
    enabled: Boolean(itemId),
  });

export const useItemTimeLogs = (itemId: string | null) =>
  useQuery({
    queryKey: ['items', itemId, 'time-logs'],
    queryFn: () => api.get<ItemTimeLog[]>(`/items/${itemId}/time-logs`),
    enabled: Boolean(itemId),
  });

/** Every item mutation refreshes both the board and the open drawer. */
export function useItemActions(boardId: string, itemId?: string | null) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.board(boardId) });
    if (itemId) void qc.invalidateQueries({ queryKey: ['items', itemId] });
  };

  return {
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        api.put(`/items/${id}`, body),
      onSuccess: invalidate,
    }),
    comment: useMutation({
      mutationFn: ({ id, content, parentCommentId, mentions }: {
        id: string; content: string; parentCommentId?: string; mentions?: string[];
      }) => api.post(`/items/${id}/comments`, { content, parentCommentId, mentions }),
      onSuccess: invalidate,
    }),
    assignees: useMutation({
      mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>
        api.put(`/items/${id}/assignees`, { userIds }),
      onSuccess: invalidate,
    }),
    duplicate: useMutation({
      mutationFn: (id: string) => api.post(`/items/${id}/duplicate`),
      onSuccess: invalidate,
    }),
    move: useMutation({
      mutationFn: ({ id, groupId, position }: { id: string; groupId: string; position?: number }) =>
        api.post(`/items/${id}/move`, { groupId, position }),
      onSuccess: invalidate,
    }),
    /** Runs a Button column's configured action. */
    press: useMutation({
      mutationFn: ({ id, columnId }: { id: string; columnId: string }) =>
        api.post(`/items/${id}/columns/${columnId}/press`),
      onSuccess: invalidate,
    }),
    /** Everyone in a department who can open the board joins a People cell. */
    addDepartment: useMutation({
      mutationFn: ({ id, columnId, departmentId }: { id: string; columnId: string; departmentId: string }) =>
        api.post<DepartmentAssignment>(`/items/${id}/columns/${columnId}/department`, { departmentId }),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
        api.post(`/items/${id}/${archived ? 'archive' : 'restore'}`),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/items/${id}`),
      onSuccess: invalidate,
    }),
    createSubitem: useMutation({
      mutationFn: ({ id, groupId, name }: { id: string; groupId: string; name: string }) =>
        api.post(`/items/${id}/subitems`, { groupId, name, values: {} }),
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: ({ groupId, itemIds }: { groupId: string; itemIds: string[] }) =>
        api.post('/items/reorder', { groupId, itemIds }),
      onSuccess: invalidate,
    }),
  };
}

/** Column definitions and their status labels. */
export function useColumnActions(boardId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.board(boardId) });

  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => api.post(`/boards/${boardId}/columns`, body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        api.put(`/columns/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/columns/${id}`),
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) => api.post(`/boards/${boardId}/columns/reorder`, { ids }),
      onSuccess: invalidate,
    }),
    addLabel: useMutation({
      mutationFn: ({ columnId, body }: { columnId: string; body: Record<string, unknown> }) =>
        api.post(`/columns/${columnId}/labels`, body),
      onSuccess: invalidate,
    }),
    updateLabel: useMutation({
      mutationFn: ({ labelId, body }: { labelId: string; body: Record<string, unknown> }) =>
        api.put(`/columns/labels/${labelId}`, body),
      onSuccess: invalidate,
    }),
    removeLabel: useMutation({
      mutationFn: (labelId: string) => api.delete(`/columns/labels/${labelId}`),
      onSuccess: invalidate,
    }),
  };
}

export function useGroupActions(boardId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.board(boardId) });

  return {
    create: useMutation({
      mutationFn: (body: { name: string; color?: string }) =>
        api.post(`/boards/${boardId}/groups`, body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        api.put(`/groups/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/groups/${id}`),
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: (groupIds: string[]) => api.post('/groups/reorder', { groupIds }),
      onSuccess: invalidate,
    }),
  };
}

export function useBoardActions(workspaceId: string, boardId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.board(boardId) });
    void qc.invalidateQueries({ queryKey: keys.boards(workspaceId) });
  };

  return {
    duplicate: useMutation({
      mutationFn: (withItems: boolean) => api.post(`/boards/${boardId}/duplicate`, { withItems }),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: () => api.delete(`/boards/${boardId}`),
      onSuccess: invalidate,
    }),
    /** Waits for the board to reload, so the dragged width never snaps back first. */
    resizeName: useMutation({
      mutationFn: (nameWidth: number) => api.put(`/boards/${boardId}`, { nameWidth }),
      onSuccess: () => qc.invalidateQueries({ queryKey: keys.board(boardId) }),
    }),
    importCsv: useMutation({
      mutationFn: (csv: string) => api.post(`/imports/boards/${boardId}`, { csv }),
      onSuccess: invalidate,
    }),
    /** The board keeps its id, so the page stays open and simply follows it. */
    move: useMutation({
      mutationFn: (body: { workspaceId: string; folderId: string | null }) =>
        api.post(`/boards/${boardId}/move`, body),
      onSuccess: (_board, body) => {
        invalidate();
        void qc.invalidateQueries({ queryKey: keys.boards(body.workspaceId) });
        // Both workspaces' board counts changed.
        void qc.invalidateQueries({ queryKey: keys.workspaces, exact: true });
      },
    }),
  };
}

export function useUpdateItemValues(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, values }: { itemId: string; values: Record<string, unknown> }) =>
      api.patch<BoardItem>(`/items/${itemId}/values`, { values }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.board(boardId) }),
  });
}

export interface ItemHoursRow {
  id: string; name: string; seconds: number; entries: number;
  firstLoggedAt: string | null; lastLoggedAt: string | null;
}

/** Time on this task, grouped by person. */
export const useItemHours = (itemId: string | null) =>
  useQuery({
    queryKey: ['reports', 'item-hours', itemId],
    queryFn: () => api.get<ItemHoursRow[]>(`/reports/items/${itemId}/hours`),
    enabled: Boolean(itemId),
  });
